import { type Theme } from '@mui/material';
import { covElevatedSurfaceBase } from '~/modules/covenants/CovenantsDataForm/commonStyles.ts';

export const clientStyles = {
  selectorWrap: (theme: Theme) => ({
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    width: '100%',
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(6),
    paddingInline: theme.spacing(2),
    background: theme.palette.background.default,
  }),
  selectorCard: (theme: Theme) => ({
    ...covElevatedSurfaceBase(theme),
    width: '100%',
    maxWidth: theme.spacing(80),
    p: theme.spacing(4),
  }),
  selectorTitle: {
    textAlign: 'center',
  },
  selectorForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  selectorSubmit: (theme: Theme) => ({
    textTransform: 'none',
    borderRadius: theme.spacing(1),
    fontWeight: 700,
    alignSelf: 'center',
    minWidth: theme.spacing(30),
  }),
  pageRoot: (theme: Theme) => ({
    paddingTop: { xs: theme.spacing(3), sm: theme.spacing(6) },
  }),
  pageHeader: (theme: Theme) => ({
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      alignItems: 'flex-start',
      flexDirection: 'column',
    },
  }),
  pageTitle: (theme: Theme) => ({
    flex: 1,
    minWidth: 0,
    fontFamily: '"Nunito Sans", sans-serif',
    fontWeight: 800,
    fontSize: { xs: '28px', md: '34px' },
    lineHeight: { xs: '100%', md: '96%' },
    letterSpacing: 0,
    color: theme.palette.text.primary,
    margin: 0,
  }),
  pageCode: (theme: Theme) => ({
    flexShrink: 0,
    fontFamily: '"Nunito Sans", sans-serif',
    fontWeight: 700,
    fontSize: { xs: '18px', md: '22px' },
    lineHeight: '100%',
    letterSpacing: 0,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('md')]: {
      whiteSpace: 'normal',
    },
  }),
  pageContainer: (theme: Theme) => ({
    maxWidth: theme.breakpoints.values.lg,
    marginInline: 'auto',
    width: '100%',
    paddingInline: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      paddingInline: theme.spacing(3),
    },
  }),
  pageHeaderDivider: (theme: Theme) => ({
    marginBottom: theme.spacing(5),
    borderColor: theme.palette.border.subtle,
  }),
  covDialogPaper: (theme: Theme) => ({
    width: 'min(540px, calc(100vw - 32px))',
    maxWidth: 'calc(100vw - 32px)',
    maxHeight: 'calc(100vh - 32px)',
    margin: theme.spacing(2),
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.spacing(1),
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'column',
  }),
  covDialogTitle: (theme: Theme) => ({
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    padding: theme.spacing(3, 3, 2),
  }),
  covDialogTitleText: {
    display: 'block',
    fontFamily: '"Nunito Sans", sans-serif',
    fontWeight: 800,
    fontSize: '24px',
    lineHeight: '24px',
    letterSpacing: 0,
  },
  covDialogCloseButton: (theme: Theme) => ({
    color: theme.palette.text.primary,
    mt: theme.spacing(-0.75),
    mr: theme.spacing(-1),
  }),
  covDialogContent: (theme: Theme) => ({
    flex: '1 1 auto',
    overflowY: 'auto',
    px: theme.spacing(3),
    pt: 0,
    pb: 0,
  }),
  covDialogBody: (theme: Theme) => ({
    borderTop: `1px solid ${theme.palette.border.subtle}`,
    pt: theme.spacing(1),
    pb: theme.spacing(0.5),
  }),
  covDialogLoader: (theme: Theme) => ({
    display: 'flex',
    justifyContent: 'center',
    py: theme.spacing(4),
  }),
  covDialogMessage: (theme: Theme) => ({
    color: theme.palette.text.secondary,
    fontSize: '0.9375rem',
    lineHeight: '22px',
    py: theme.spacing(2.5),
  }),
  covDialogErrorMessage: (theme: Theme) => ({
    color: theme.palette.error.main,
    fontSize: '0.9375rem',
    lineHeight: '22px',
    py: theme.spacing(2.5),
  }),
  covDialogList: {
    display: 'flex',
    flexDirection: 'column',
  },
  covDialogItem: (theme: Theme) => ({
    borderBottom: `1px solid ${theme.palette.border.subtle}`,
  }),
  covDialogItemExpanded: (theme: Theme) => ({
    borderBottomColor: theme.palette.primary.main,
  }),
  covDialogItemButton: (theme: Theme) => ({
    width: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.75, 0),
    textAlign: 'left',
    borderRadius: 0,
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.primary['200'],
    },
  }),
  covDialogItemIcon: (theme: Theme) => ({
    flexShrink: 0,
    color: theme.palette.text.secondary,
    fontSize: 22,
    mt: theme.spacing(0.125),
  }),
  covDialogItemMeta: {
    flex: 1,
    minWidth: 0,
  },
  covDialogItemTitle: (theme: Theme) => ({
    fontWeight: 700,
    fontSize: '0.9375rem',
    lineHeight: '22px',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.25),
  }),
  covDialogItemSubtitle: (theme: Theme) => ({
    fontSize: '0.9375rem',
    lineHeight: '22px',
    color: theme.palette.text.secondary,
  }),
  covDialogItemChevron: (theme: Theme) => ({
    flexShrink: 0,
    color: theme.palette.text.primary,
    fontSize: 24,
    mt: theme.spacing(0.125),
  }),
  covDialogItemDetails: (theme: Theme) => ({
    paddingLeft: theme.spacing(4.75),
    paddingRight: theme.spacing(0.5),
    paddingBottom: theme.spacing(2),
  }),
  covDialogItemDetailsText: (theme: Theme) => ({
    fontFamily: '"Nunito Sans", sans-serif',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '140%',
    letterSpacing: 0,
    color: theme.palette.text.primary,
  }),
  covDialogPaginationWrap: (theme: Theme) => ({
    paddingTop: theme.spacing(1.5),
    '& > .MuiBox-root': {
      paddingLeft: 0,
      paddingRight: 0,
    },
  }),
  covDialogActions: (theme: Theme) => ({
    display: 'flex',
    justifyContent: 'center',
    px: theme.spacing(3),
    pt: theme.spacing(2.5),
    pb: theme.spacing(3),
  }),
  covDialogCloseAction: (theme: Theme) => ({
    minWidth: 100,
    borderRadius: 999,
    border: `1px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
    textTransform: 'none',
    fontWeight: 500,
    px: theme.spacing(3),
    py: theme.spacing(1.125),
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      minHeight: 48,
      borderRadius: 999,
    },
  }),
  createTitle: (theme: Theme) => ({
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    padding: theme.spacing(3, 3, 1),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(3, 3, 1),
    },
  }),
  createHeaderContent: {
    minWidth: 0,
  },
  createDialogPaper: (theme: Theme) => ({
    width: 'min(628px, calc(100vw - 32px))',
    maxWidth: 'calc(100vw - 32px)',
    margin: theme.spacing(2),
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.spacing(1),
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'column',
  }),
  createTitleText: (theme: Theme) => ({
    display: 'block',
    fontFamily: '"Nunito Sans", sans-serif',
    fontWeight: 800,
    fontSize: '24px',
    lineHeight: '24px',
    letterSpacing: 0,
    marginBottom: theme.spacing(0.75),
  }),
  createSubtitle: (theme: Theme) => ({
    mb: 0,
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  }),
  createCloseButton: (theme: Theme) => ({
    color: theme.palette.text.primary,
    mt: theme.spacing(-0.75),
    mr: theme.spacing(-1),
  }),
  createContent: (theme: Theme) => ({
    flex: '0 0 auto',
    overflowY: 'visible',
    px: theme.spacing(3),
    pb: 0,
    pt: 0,
    [theme.breakpoints.down('sm')]: {
      px: theme.spacing(3),
    },
  }),
  createBody: {},
  createDivider: (theme: Theme) => ({
    borderColor: theme.palette.border.subtle,
  }),
  createList: (theme: Theme) => ({
    pt: theme.spacing(1.5),
    minHeight: 256,
  }),
  createListRow: (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    columnGap: theme.spacing(2),
    rowGap: theme.spacing(1),
    py: theme.spacing(1.25),
    [theme.breakpoints.down('md')]: {
      alignItems: 'flex-start',
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  }),
  createListMeta: {
    flex: 1,
    minWidth: 0,
    maxWidth: '100%',
  },
  createListTitle: (theme: Theme) => ({
    fontWeight: 700,
    mb: theme.spacing(0.5),
  }),
  createListDate: (theme: Theme) => ({
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  }),
  createListBtn: (theme: Theme) => ({
    borderRadius: 999,
    minWidth: 154,
    flexShrink: 0,
    px: theme.spacing(2),
    py: theme.spacing(0.875),
    textTransform: 'none',
    fontWeight: 500,
    color: theme.palette.text.primary,
    borderColor: theme.palette.border.subtle,
    backgroundColor: theme.palette.common.white,
    boxShadow: 'none',
    '&:hover': {
      borderColor: theme.palette.border.subtle,
      backgroundColor: theme.palette.primary['200'],
      color: theme.palette.text.primary,
      boxShadow: 'none',
    },
    '&.Mui-focusVisible': {
      boxShadow: 'none',
    },
    '& .MuiButton-endIcon': {
      color: 'inherit',
    },
    [theme.breakpoints.down('md')]: {
      minWidth: 144,
      alignSelf: 'flex-start',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      minWidth: 0,
      minHeight: 44,
      borderRadius: 999,
      justifyContent: 'space-between',
    },
  }),
  createPaginationWrap: (theme: Theme) => ({
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    '& > .MuiBox-root': {
      paddingLeft: 0,
      paddingRight: 0,
      paddingTop: 0,
      paddingBottom: 0,
    },
    '& .MuiPagination-ul': {
      gap: theme.spacing(0.5),
    },
    '& .MuiPaginationItem-root': {
      minWidth: 32,
      width: 32,
      height: 32,
      borderRadius: '999px',
      margin: 0,
      border: 'none',
      backgroundColor: 'transparent',
      color: theme.palette.text.primary,
      fontSize: '1rem',
      lineHeight: '20px',
      '&:hover': {
        backgroundColor: 'transparent',
      },
      '&.Mui-selected': {
        backgroundColor: theme.palette.primary['200'],
        color: theme.palette.primary.main,
        fontWeight: 600,
      },
      '&.Mui-selected:hover': {
        backgroundColor: theme.palette.primary['200'],
      },
      '&.MuiPaginationItem-previousNext': {
        color: theme.palette.text.secondary,
      },
    },
  }),
  createActions: (theme: Theme) => ({
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(2),
    px: theme.spacing(3),
    pt: theme.spacing(3),
    pb: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      px: theme.spacing(3),
      pt: theme.spacing(3),
      pb: theme.spacing(3),
    },
  }),
  createCancelBtn: (theme: Theme) => ({
    minWidth: 110,
    borderRadius: 999,
    border: `1px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
    textTransform: 'none',
    fontWeight: 500,
    boxShadow: 'none',
    px: theme.spacing(2.5),
    py: theme.spacing(1.25),
    '&:hover': {
      boxShadow: 'none',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      minHeight: 48,
      borderRadius: 999,
    },
  }),
  createBtn: (theme: Theme) => ({
    borderRadius: 999,
    minWidth: 190,
    px: theme.spacing(2.5),
    py: theme.spacing(1.25),
    textTransform: 'none',
    fontWeight: 500,
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      boxShadow: 'none',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      minHeight: 48,
      borderRadius: 999,
    },
  }),
} as const;
